import { useEffect, useState } from 'react';
import { api, notifications as Api, type Channel, type Notification, type NotificationInput,
  type NotificationSend, type PushPlatform, type SendSummary } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim, SkeletonRows } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { Modal } from '../components/Modal';
import { Field, Row } from '../components/Field';

const BLANK: NotificationInput = {
  title: '', message: '', deep_link: '', category: 'Announcement',
  segment: 'All members', channels: [], platforms: ['web', 'mobile'], state: 'draft',
};

const PLATFORM_LABEL: Record<PushPlatform, string> = { web: 'Web', mobile: 'Mobile app' };

/** "18 Sep, 7:12 pm" in the market's own timezone. */
const when = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(String(iso).replace(' ', 'T') + 'Z');
  return isNaN(d.valueOf()) ? String(iso) : d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
  });
};

/** How far a send has got. A send with nothing to send to is finished. */
function Progress({ send }: { send: NotificationSend }) {
  const pct = send.targeted ? Math.round((send.processed / send.targeted) * 100) : 100;
  const done = send.state !== 'running';
  return (
    <div>
      <div style={{ height: 6, borderRadius: 999, background: 'rgba(var(--wash), calc(.08 * var(--washx)))', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, transition: 'width .3s',
          background: send.state === 'failed' ? 'var(--down)' : done ? 'var(--up)' : 'var(--gold)' }} />
      </div>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
        {send.state === 'running'
          ? `Sending — ${send.processed} of ${send.targeted} devices`
          : `${send.sent} delivered · ${send.failed} failed of ${send.targeted} devices`}
        {send.pruned ? ` · ${send.pruned} dead device${send.pruned === 1 ? '' : 's'} dropped` : ''}
        {send.held ? ` · ${send.held} held (topic off for push)` : ''}
      </div>
    </div>
  );
}

/** Sent or not, and when — what the list used to call "queued". */
function SentTag({ summary }: { summary?: SendSummary }) {
  const last = summary?.last;
  if (!last) return <span className="tg t-nu">Not sent</span>;
  if (last.state === 'running') return <span className="tg t-gd">Sending…</span>;
  return (
    <div>
      <span className={`tg ${last.state === 'failed' ? 't-dn' : 't-up'}`}>
        {last.state === 'failed' ? 'Failed' : 'Sent'}
      </span>
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        {when(last.finished_at || last.started_at)}
        {summary && summary.times > 1 ? ` · ${summary.times} times` : ''}
      </div>
    </div>
  );
}

export function Notifications() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [editing, setEditing] = useState<Notification | 'new' | null>(null);
  const [viewing, setViewing] = useState<Notification | null>(null);
  const items = data?.items ?? [];
  const sends = data?.sends ?? {};

  const sel = useSelection(items);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Notifications</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7, maxWidth: 760, lineHeight: 1.55 }}>
            Saving writes a notice; <strong>Send</strong> is what delivers it. Push goes to members who allowed
            notifications on the devices you pick. Telegram, WhatsApp and email have no provider connected yet, so
            those channels still only record the intent.
            {data && !data.configured
              ? ' Push is not configured on the server yet, so Send is unavailable.' : ''}
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditing('new')}>New notification</button>
      </div>

      {error ? <div className="err">Could not load notifications — {error.message}</div> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <BulkActions
          path="/notifications" noun="notification"
          rows={sel.picked.map((n) => ({ id: n.id, label: String(n.title) }))}
          actions={[['publish', 'Show in the app'], ['unpublish', 'Hide from the app']]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} /><th>Title</th><th>Category</th><th>Who</th><th>Channels</th><th>Devices</th><th>Sent</th><th /></tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={8} tick />
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="muted" style={{ padding: '26px 8px' }}>Nothing written yet.</td></tr>
              ) : items.map((n) => (
                <tr key={n.id} style={sel.isOn(n.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(n.id)} label={String(n.title)}
                    onChange={(v) => sel.toggle(n.id, v)} />
                  <td><div style={{ fontWeight: 700 }}>{n.title}</div>
                    {n.message ? (
                      <div style={{ fontSize: 11.5, color: 'var(--tx3)', maxWidth: 420, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</div>
                    ) : null}</td>
                  <td className="muted">{n.category}</td>
                  <td className="muted">{n.segment}</td>
                  <td className="muted">{n.channels || '—'}</td>
                  <td className="muted">{(n.platforms || 'web,mobile').split(',')
                    .map((p) => PLATFORM_LABEL[p.trim() as PushPlatform] || p).join(' + ')}</td>
                  <td><SentTag summary={sends[n.id]} /></td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => setViewing(n)}>View</button>
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5, marginLeft: 6 }}
                      type="button" onClick={() => setEditing(n)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewing ? (
        <SendLog
          notification={viewing}
          onClose={async () => { setViewing(null); await reload(); }}
        />
      ) : null}

      {editing ? (
        <NotificationForm
          notification={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function NotificationForm({ notification, onCancel, onSaved }: {
  notification: Notification | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [f, setF] = useState<NotificationInput>(() => notification ? {
    title: notification.title, message: notification.message, deep_link: notification.deep_link,
    category: notification.category, segment: notification.segment,
    channels: notification.channels ? (notification.channels.split(',') as Channel[]) : [],
    platforms: (notification.platforms || 'web,mobile').split(',').map((p) => p.trim()) as PushPlatform[],
    state: notification.state,
  } : BLANK);
  const [channels, setChannels] = useState<Channel[]>(['Push', 'Telegram', 'Email', 'WhatsApp']);
  const [segments, setSegments] = useState<string[]>(['All members']);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    void api.get<{ channels: Channel[]; notifySegments: string[] }>('/meta')
      .then((m) => {
        if (m.channels) setChannels(m.channels);
        if (m.notifySegments) setSegments(m.notifySegments);
      }).catch(() => {});
  }, []);

  const set = <K extends keyof NotificationInput>(k: K, v: NotificationInput[K]) =>
    setF((p) => ({ ...p, [k]: v }));

  async function submit(state: 'draft' | 'queued') {
    if (!f.title.trim()) { setError('A title is required.'); return; }
    setError('');
    setStatus(state === 'queued' ? 'Queueing…' : 'Saving…');
    try {
      if (notification) await Api.update(notification.id, { ...f, state });
      else await Api.create({ ...f, state });
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={notification ? `Edit · ${notification.title}` : 'New notification'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('queued')}>Save &amp; show in the app</button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <Field label="Title"><input className="inp" value={f.title} autoFocus
        onChange={(e) => set('title', e.target.value)} /></Field>
      <Field label="Message"><textarea className="inp ta" rows={3} value={f.message}
        onChange={(e) => set('message', e.target.value)} /></Field>

      <Row>
        <Field label="Category"><input className="inp" value={f.category}
          onChange={(e) => set('category', e.target.value)} /></Field>
        <Field label="Who it is for"
          hint="A saved notice appears in that group's bell in the member area; a push goes to the same group.">
          <select className="inp" value={f.segment} onChange={(e) => set('segment', e.target.value)}>
            {segments.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Opens"><input className="inp" value={f.deep_link} placeholder="/trade-calls/"
          onChange={(e) => set('deep_link', e.target.value)} /></Field>
      </Row>

      <Field label="Devices" hint="Which kinds of device a push goes to. Members who only use the website are Web.">
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', paddingTop: 4 }}>
          {(['web', 'mobile'] as PushPlatform[]).map((p) => (
            <label key={p} style={{ display: 'flex', gap: 7, alignItems: 'center', cursor: 'pointer', fontSize: 13.5 }}>
              <input type="checkbox" checked={f.platforms.includes(p)} onChange={(e) => set('platforms',
                e.target.checked ? [...f.platforms, p] : f.platforms.filter((x) => x !== p))} />
              {PLATFORM_LABEL[p]}
            </label>
          ))}
        </div>
      </Field>

      <Field label="Channels" hint="Only Push delivers today. The rest record the intent until a provider is connected.">
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', paddingTop: 4 }}>
          {channels.map((c) => (
            <label key={c} style={{ display: 'flex', gap: 7, alignItems: 'center', cursor: 'pointer', fontSize: 13.5 }}>
              <input type="checkbox" checked={f.channels.includes(c)} onChange={(e) => set('channels',
                e.target.checked ? [...f.channels, c] : f.channels.filter((x) => x !== c))} />
              {c}
            </label>
          ))}
        </div>
      </Field>
    </Modal>
  );
}

/**
 * Every time this notice has gone out: how many devices, how many people, how
 * it ended — and Send, which is the only thing that delivers it.
 *
 * A running send is followed by re-reading the same rows every second, so the
 * bar moves without the server having to hold anything open.
 */
function SendLog({ notification, onClose }: { notification: Notification; onClose: () => void }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof Api.sends>> | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setData(await Api.sends(notification.id)); } catch (e) { setError((e as Error).message); }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [notification.id]);

  /* Poll only while something is actually in flight. */
  const running = !!data?.sends.some((s) => s.state === 'running');
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => { void load(); }, 1000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [running]);

  async function send() {
    setBusy(true);
    setError('');
    try {
      await Api.send(notification.id);
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  const pushable = notification.channels.toLowerCase().split(',').map((c) => c.trim()).includes('push');
  const audience = data?.audience;

  /* A notice written without Push cannot be pushed — but saying only that
     leaves the admin to guess which screen fixes it. */
  async function addPush() {
    setBusy(true);
    setError('');
    try {
      const channels = [...notification.channels.split(',').map((c) => c.trim()).filter(Boolean), 'Push'] as Channel[];
      await Api.update(notification.id, {
        title: notification.title, message: notification.message, deep_link: notification.deep_link,
        category: notification.category, segment: notification.segment, channels,
        platforms: (notification.platforms || 'web,mobile').split(',').map((p) => p.trim()) as PushPlatform[],
        state: notification.state,
      });
      notification.channels = channels.join(',');
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <Modal
      title={`Sends · ${notification.title}`}
      onClose={onClose}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>
            {!pushable ? 'This notice has no Push channel, so there is nothing to deliver.'
              : data && !data.configured ? 'Push is not configured on the server yet.'
                : audience ? `${audience.devices} device${audience.devices === 1 ? '' : 's'} · ${audience.members} member${audience.members === 1 ? '' : 's'}${audience.held ? ` · ${audience.held} held` : ''}` : ''}
          </span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onClose}>Close</button>
          {!pushable ? (
            <button className="b bo" type="button" disabled={busy} onClick={() => void addPush()}>
              {busy ? 'Adding…' : 'Add Push to this notice'}
            </button>
          ) : null}
          <button className="b bg" type="button" disabled={busy || running || !pushable || !!(data && !data.configured)}
            onClick={() => void send()}>
            {busy ? 'Starting…' : running ? 'Sending…' : data?.sends.length ? 'Send again' : 'Send now'}
          </button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      {!data ? <div style={{ display: 'grid', gap: 10 }}><Shim h={14} w="60%" /><Shim h={44} /><Shim h={44} /></div>
        : data.sends.length === 0 ? (
          <p className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
            This notice has never been sent. It goes to <strong>{notification.segment}</strong> on{' '}
            {(notification.platforms || 'web,mobile').split(',').map((p) => PLATFORM_LABEL[p.trim() as PushPlatform] || p).join(' and ')}
            {audience ? ` — ${audience.devices} device${audience.devices === 1 ? '' : 's'} right now.` : '.'}
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
              Sent {data.sends.length} time{data.sends.length === 1 ? '' : 's'}.
            </p>
            {data.sends.map((s) => (
              <div key={s.id} className="crd" style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                  alignItems: 'baseline', marginBottom: 10 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {when(s.started_at)}
                    <span className="muted" style={{ fontWeight: 400 }}>
                      {' · '}{s.segment}{' · '}{s.platforms.split(',').map((p) => PLATFORM_LABEL[p.trim() as PushPlatform] || p).join(' + ')}
                    </span>
                  </div>
                  <span className={`tg ${s.state === 'failed' ? 't-dn' : s.state === 'running' ? 't-gd' : 't-up'}`}>
                    {s.state === 'running' ? 'Sending' : s.state === 'failed' ? 'Failed' : 'Done'}
                  </span>
                </div>
                <Progress send={s} />
                <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
                  {s.members} member{s.members === 1 ? '' : 's'}{s.by_user ? ` · sent by ${s.by_user}` : ''}
                </div>
                {s.error ? <div className="muted" style={{ fontSize: 11.5, marginTop: 6, color: 'var(--goldtx)' }}>{s.error}</div> : null}
              </div>
            ))}
          </div>
        )}
    </Modal>
  );
}
